import { datadogRum } from "@datadog/browser-rum";
import { keepPreviousData } from "@tanstack/react-query";
import flatten from "lodash/flatten";
import isEmpty from "lodash/isEmpty";
import uniqBy from "lodash/uniqBy";
import { useActivityFormStore } from "PFApp/activities/activity_form/activity_form_store";
import {
  FIELD_DESCRIPTION_KEY,
  FIELD_NAME_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { Accordion } from "PFComponents/accordion/accordion";
import { Button } from "PFComponents/button";
import { Inline, Stack } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { LoadingDots } from "PFComponents/loading_dots";
import { Typography } from "PFComponents/typography";
import { getId } from "PFCore/helpers/get_id";
import { useDebounce } from "PFCore/helpers/use_debounce";
import { useKeywords } from "PFCore/hooks/queries/bookings/keywords/use_keywords";
import { KeywordsParams } from "PFCore/services/keywords";
import { Experience, Id, Importance, Keyword, PureCustomValue } from "PFTypes";
import { useEffect, useMemo, useState } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import css from "./suggestions_box.module.scss";

type SuggestionsBoxProps = {
  attributes: ((Keyword | PureCustomValue) & { created?: boolean })[];
  onMatchAttributeChange: (attr: Keyword[]) => void;
  userId: Id;
  type: string;
};

const SUGGESTIONS_MAX = 5;

export const SuggestionsBox = ({ attributes, onMatchAttributeChange, type, userId }: SuggestionsBoxProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "form.suggestedAttributes" });
  const { isNew } = useActivityFormStore((state) => state.activityStatus);
  const [name, description] = useWatch({ name: [FIELD_NAME_KEY, FIELD_DESCRIPTION_KEY] });
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);

  const attributesIds = useMemo(() => attributes.filter(({ created }) => !created).map(getId), [attributes]);
  const usedOn = isNew ? "Activity_creation" : "Activity_edition";

  const getParams = () => {
    const params: KeywordsParams = {
      text: `${name} ${description}`,
      matchTypes: [type]
    };
    if (attributesIds.length > 0) {
      params.selectedIds = attributesIds;
    }
    return params;
  };

  const [params, setParams] = useState(getParams());
  const debounceSetMatchAttributes = useDebounce(() => {
    setParams(getParams());
  }, 500);

  useEffect(() => {
    debounceSetMatchAttributes();
  }, [name, description, attributes]);

  const showSuggestions = Boolean(Number(params.selectedIds?.length) > 0 || params.text?.trim());

  const { data, isFetching } = useKeywords(params, {
    enabled: showSuggestions,
    placeholderData: showSuggestions ? keepPreviousData : undefined
  });

  const suggestions = useMemo(
    () =>
      ((data?.entries || []) as Keyword[])
        .filter((item) => !(attributesIds || []).includes(getId(item)))
        .slice(0, showAllSuggestions ? undefined : SUGGESTIONS_MAX),
    [attributesIds, data?.entries, showAllSuggestions]
  );

  const hasMoreSuggestions = (data?.entries?.length || 0) > suggestions.length;

  const handleMatchAttributeChange = (selectedAttribute) => {
    const dedupedAttributes = uniqBy(
      flatten([...attributes, selectedAttribute]),
      (attribute: Keyword | PureCustomValue) => ("value" in attribute ? attribute.value : attribute.text)
    );
    const decoratedAttributes = dedupedAttributes.map((value) => ({
      text: value.value,
      importance: Importance.Essential,
      required_experience: Experience.Intermediate,
      ...value
    }));
    onMatchAttributeChange(decoratedAttributes);
  };

  if (isEmpty(suggestions)) {
    return null;
  }

  return (
    <div
      className={css.suggestionsBox}
      style={{ opacity: isFetching ? 0.5 : 1 }}
      data-qa-id="attribute-suggestions"
    >
      <Accordion
        header={t("title")}
        headerVariant="h5"
        actions={isFetching && <LoadingDots />}
        onStateChange={() => setShowAllSuggestions(false)}
        animated={false}
      >
        <Stack>
          <Typography variant="bodyRegular">{t("identifiedRelatedAttributes")}</Typography>
          <Inline gap="spacingSm" wrap="wrap">
            {suggestions.map((suggestion) => (
              <Button
                className={css.matchAttributesButton}
                key={`${suggestion.id}+${suggestion.globalId}`}
                kind="secondary"
                onClick={() => {
                  handleMatchAttributeChange(suggestion);
                  datadogRum.addAction(`${usedOn}-recommendation`, {
                    userId,
                    searchText: `${name} ${description}`,
                    recommendations: suggestions,
                    selectedRecommendation: suggestion.value,
                    index: suggestions.findIndex((item) => item.value === suggestion.value),
                    timestamp: new Date().getTime()
                  });
                }}
              >
                <Icon name="add" size="xxs" />
                {suggestion.value}
              </Button>
            ))}
            {hasMoreSuggestions && (
              <Button kind="text" onClick={() => setShowAllSuggestions(true)}>
                ...
              </Button>
            )}
          </Inline>
        </Stack>
      </Accordion>
    </div>
  );
};
